import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { BsChevronUp, BsChevronDown } from "react-icons/bs";
import LinkButton from "~/shared/LinkButton";

const progressBarStripes = keyframes`
    from {
        background-position: 40px 0;
    }

    to {
        background-position: 0 0;
    }
`;

const Drawer = styled.div`
    width: 350px;
    border: 1px solid;
    border-bottom: none;
    position: fixed;
    bottom: 0;
    background: #fff;
    z-index: 1;

    .header {
        background: #4b4b4b;
        color: #fff;
        padding: 10px;

        button {
            color: #fff;
            font-weight: normal;
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        }

        button:hover {
            text-decoration: none;
        }
    }

    .body .inner-content {
        padding: 10px;
    }

    .item {
        padding: 0px 0 15px 0;
        border-bottom: 1px solid #dcdcdc;
        margin-bottom: 10px;
    }

    .progress-bar {
        width: 100%;
        height: 10px;
        border: 1px solid #dcdcdc;
        border-radius: 20px;
        overflow: hidden;
    }

    .progress {
        width: 50%;
        height: 100%;
        background: ${({ theme }) => theme.color.primary.background};
        background-image: linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent) !important;
        background-size: 1rem 1rem !important;
        animation: ${progressBarStripes} 1s linear infinite;
    }
`;

type DownloadDrawerProps = {
    open?: boolean;
    bundle?: () => void;
    downloadProgress: {
        imagesTotal: number;
        imagesDownloaded: number;
        videosTotal: number;
        videosDownloaded: number;
        othersTotal: number;
        othersDownloaded: number;
    }
};

let timeout = null;

const DownloadDrawer = ({ downloadProgress, bundle = undefined, open = false }: DownloadDrawerProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [showBundle, setShowBundle] = useState(false);

    useEffect(() => {
        setIsOpen(open);

        if (bundle && open) {
            timeout = setTimeout(() => {
                setShowBundle(true);
            }, 5 * 1000 * 60);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        }
    }, [open]);

    const getPercent = (type: string) => {
        const totalEl = `${type}Total`;
        const downloadedEl = `${type}Downloaded`;

        if (downloadProgress[totalEl] > 0) {
            const percent = downloadProgress[downloadedEl] / downloadProgress[totalEl] * 100;
            return percent || 5;
        }

        return 1;
    };

    return (
        <Drawer>
            <div className="header">
                <LinkButton onClick={() => setIsOpen(o => !o)}>
                    Download
                    {isOpen ? <BsChevronDown /> : <BsChevronUp />}
                </LinkButton>
            </div>
            <div className="body">
                {isOpen ? (
                    <div className="inner-content">
                        {downloadProgress.imagesTotal ? (
                            <div className="item">
                                <div className="progress-bar">
                                    <div className="progress" style={{ width: `${getPercent("images")}%` }} />
                                </div>
                                <p>
                                    <small>{downloadProgress.imagesDownloaded} / {downloadProgress.imagesTotal} - Baixando e compactando imagens</small>
                                </p>
                            </div>
                        ) : (
                            ""
                        )}

                        {downloadProgress.videosTotal ? (
                            <div className="item">
                                <div className="progress-bar">
                                    <div className="progress" style={{ width: `${getPercent("videos")}%` }} />
                                </div>
                                <p>
                                    <small>{downloadProgress.videosDownloaded} / {downloadProgress.videosTotal} - Baixando e compactando vídeos. Esse processo pode demorar um pouco</small>
                                </p>
                            </div>
                        ) : (
                            ""
                        )}

                        {downloadProgress.othersTotal ? (
                            <div className="item">
                                <div className="progress-bar">
                                    <div className="progress" style={{ width: `${getPercent("others")}%` }} />
                                </div>
                                <p>
                                    <small>{downloadProgress.othersDownloaded} / {downloadProgress.othersTotal} - Baixando e compactando outros arquivos</small>
                                </p>
                            </div>
                        ) : (
                            ""
                        )}

                        {showBundle && bundle ? (
                            <p>
                                <small>
                                    Demorando muito para o download iniciar: <LinkButton onClick={bundle} style={{ fontSize: "0.85rem" }}>clique aqui</LinkButton>
                                </small>
                            </p>
                        ) : (
                            ""
                        )}
                    </div>
                ) : (
                    ""
                )}
            </div>
        </Drawer>
    );
};

export default DownloadDrawer;
